import { makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePaginationFooter from 'components/TablePaginationFooter';
import TableRowSkeleton from 'components/TableRowSkeleton';
import { Message } from 'generated/graphql';
import React, { useEffect } from 'react';
import { usePagination, useTable } from 'react-table';
import { columns } from './LogTableColumns';
import { useMsgs } from './useMsgs';

interface Props {}

const useStyles = makeStyles((theme: Theme) => ({
  noBottomBorder: { borderBottom: 0 },
  head: {
    backgroundColor: theme.palette.grey[300],
  },
}));

const LogTable = (props: Props) => {
  const classes = useStyles(props);

  const {
    isLoading,
    data,
    count,
    pageCount,
    setParams,
    pageParam,
    rowsParam,
  } = useMsgs();

  const tableInstance = useTable<Message>(
    {
      columns,
      data,
      manualPagination: true,
      pageCount,
      initialState: { pageIndex: pageParam - 1, pageSize: rowsParam },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    setPageSize,
    prepareRow,
    state: { pageIndex, pageSize },
  } = tableInstance;

  useEffect(() => {
    // Check if first load
    const isSamePageIndex = pageIndex === pageParam - 1;
    const isSamePageSize = pageSize === rowsParam;

    if (!isSamePageIndex || !isSamePageSize) {
      setParams({ page: `${pageIndex + 1}`, rows: `${pageSize}` });
    }
  }, [pageIndex, pageSize, setParams, pageParam, rowsParam]);

  return (
    <TableContainer>
      <Table size="small" {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow
              className={classes.head}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column) => (
                <TableCell {...column.getHeaderProps()}>
                  {column.render('Header')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {isLoading ? (
            <TableRowSkeleton pageSize={pageSize} columns={columns} />
          ) : (
            page.map((row) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <TableCell {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
        <TablePaginationFooter
          colSpan={columns.length}
          count={count}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          gotoPage={gotoPage}
          setPageSize={setPageSize}
        />
      </Table>
    </TableContainer>
  );
};

export default LogTable;
